import {Component, Event, type EventEmitter, h, Prop, State} from '@stencil/core';
import {ChipType, type GuidedChipEventDetail} from './model';

export interface ChipItem{
  /**
   * Translated label, to be displayed for UI
   */
  label: string,
  /**
   * Source label name, internal
   */
  name: string,
}

const isChipItem = (item:ChipItem|string): item is ChipItem => {
    if(item == null)
      return false;
    if(typeof item == 'string')
      return false;
    if('label' in item && 'name' in item)
      return true;
};

const getAsChipItem = (item:ChipItem|string): ChipItem => {
    if(isChipItem(item))
      return item;
    return {
      label: item,
      name: item
    };
};

@Component({
  tag: 'uiaas-guided-v2-chips-carousel',
  shadow: false,
})
export class GuidedV2ChipsCarousel {

  /**
   * Header to display above carousel
   */
  @Prop() readonly header: string;

  /**
  * List of strings to display them as chips in carousel.
  * Probably will be changed to list of objects in near future.
  */
  @Prop() readonly terms: Array<ChipItem|string>|string = [];
  #terms: ChipItem[] = [];

  /**
   * Enables availability to select chip.
   * If disabled shows icon
   */
  @Prop() readonly isSelectable: boolean = false;

  /**
   * Allows to select element on first load,
   * must match ChipItem.name
   */
  @Prop() readonly initialSelection: string;

  /**
   * maximum tag text length for desktop view
   */
  @Prop() readonly maxDesktopTagLength: number = -1;

  /**
   * maximum tag text length for mobile view
   */
  @Prop() readonly maxMobileTagLength: number = -1;

  /**
   * Is mobile view
   */
  @Prop() readonly isMobile: boolean = false;

  /**
   * Is promoted results
   */
  @Prop() readonly isPromoted: boolean = false;

  /**
   * Chip type is for events
   */
  @Prop() readonly chipType: ChipType = ChipType.None;

  /**
   * Passed to the event emitter used when user clicks on the chip
   */
  @Prop() readonly chipClickedSourceType: string;

  /**
   * Country of the chip
   */
  @Prop() readonly country?: string;
  /**
   * Language of the chip
   */
  @Prop() readonly language?: string;

  /**
   * Flag to indicate if the text is truncated
   */
  @State() isTruncated: boolean = false;

  /**
   * Event triggered after chip click
   */
  @Event({
    eventName: 'guided:chipClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    chipClickedEmitter: EventEmitter<GuidedChipEventDetail>;

  /**
   * Event triggered after chip click. Druty?
   */
    @Event({
      eventName: 'guided2:ChipClicked',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      guided2ChipClickedEmitter: EventEmitter<GuidedChipEventDetail>;

    private chips = [];

    private handleKeydown = (ev:KeyboardEvent) => {
        if (['Space', 'Enter'].includes(ev.code)) {
          ev.preventDefault();
          ev.stopPropagation();
          ev.stopImmediatePropagation();
          this.handleChipSelect(ev);
        }
    };



    private handleClick = (ev:MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();

        this.handleChipSelect(ev);
    };

    private handleChipSelect = (ev: MouseEvent|KeyboardEvent) => {
        const target = ev.target as HTMLElement & {text:string, basename: string, checked:boolean};
        target.scrollIntoView({behavior:'smooth', block: 'nearest', inline:'center'});

        if (this.isSelectable) {
          this.chips.forEach(c=>{
              if(c) {
                c.checked = false;
              }}
          );
          target.checked = true;
        }

        this.chipClickedEmitter.emit({
          label:target.text,
          baseName: target.getAttribute('basename'),
          chipType: this.chipType,
          sourceType: this.chipClickedSourceType,
          isPromoted: this.isPromoted,
        });
        this.guided2ChipClickedEmitter.emit({
          label:target.text,
          baseName: target.getAttribute('basename'),
          chipType: this.chipType,
          sourceType: this.chipClickedSourceType,
          isPromoted: this.isPromoted,
          country: this.country,
          language: this.language
        });
    };

    private getItems(): ChipItem[]{
        return this.#terms.map(getAsChipItem);
    }

    componentWillLoad() {
        this.#terms = typeof this.terms == 'string' ? JSON.parse(this.terms) : this.terms;
        this.chips = this.#terms.map(()=>{
            return {
              checked: false
            };
        });
    }

    componentDidLoad() {
        if (this.initialSelection && this.isSelectable) {


          const chipIndex = this.#terms.findIndex(t=>t.name === this.initialSelection);
          if (chipIndex >= 0) {
            this.chips[chipIndex].checked = true;
          }
        }
    }

    private truncateText = (text: string, maxDesktopChar: number, maxMobileChar: number): string => {

        if(text.length == 0) return;
        if(maxDesktopChar === -1 || maxMobileChar === -1) return text;

        const maxChar = this.isMobile ? maxMobileChar : maxDesktopChar;

        if (text.length <= maxChar) {
          this.isTruncated = false;
          return text;
        }

        // Check if the last word is complete to avoid cutting off the text wihout last word
        const isLastWordComplete = text.substring(0, maxChar + 1).endsWith(' ');

        let truncated = text.substring(0, maxChar);
        const lastSpaceIndex = truncated.lastIndexOf(' ');

        if (lastSpaceIndex > 0 && !isLastWordComplete) {
          truncated = truncated.substring(0, lastSpaceIndex);
        }

        this.isTruncated = true;
        return truncated + '...';
    };


    render() {
        const terms = this.getItems();

        if(terms.length == 0)
          return '';

        return <svs-carousel hidable-arrows={!this.isMobile}>
          {this.header && this.header !== '' &&
            <div class="guided-v2-carousel-header" slot="header">{this.header}</div>}
          {terms.map((label: ChipItem, i) => {
              return <qds-chip
                ref={c => this.chips[i] = c}
                title={label?.label}
                size="small"
                text={this.truncateText(label?.label, this.maxDesktopTagLength, this.maxMobileTagLength)}
                basename={label.name}
                icon-name={this.isPromoted ? 'fire' : this.isSelectable ? '' : 'magnifying-glass'}
                onClickCapture={this.handleClick}
                onKeydownCapture={this.handleKeydown}
              ></qds-chip>;
          }
          )}
          {/* Dummy div to provide right padding */}
          <div>&nbsp;</div>
        </svs-carousel>;
    }
}
